/**
 * ScrollbarHandler
 * Renders a thin overlay scrollbar that doesn't affect layout.
 * Tracks the active scroll container — overlay panels on single/lightbox pages,
 * falls back to the document on regular pages.
 * Supports click-on-track and drag-to-scroll.
 */
export default class ScrollbarHandler {
  private track: HTMLDivElement;
  private thumb: HTMLDivElement;
  private scrollContainer: HTMLElement | null = null;
  private isWindowScroll = false;
  private boundUpdate: (() => void) | null = null;

  // Drag state
  private isDragging = false;
  private dragStartY = 0;
  private dragStartScrollTop = 0;

  constructor() {
    this.track = document.createElement("div");
    this.track.className = "custom-scrollbar-track";

    this.thumb = document.createElement("div");
    this.thumb.className = "custom-scrollbar-thumb";

    this.track.appendChild(this.thumb);
    document.body.appendChild(this.track);

    this.initDrag();

    this.attachToCurrentContainer();

    // Recalculate after full page load — images may have changed scrollHeight
    window.addEventListener("load", () => this.updateThumb());

    window.addEventListener("popstate", () => this.handleNavigationChange());
    window.addEventListener("lightboxContentLoaded", () =>
      this.handleNavigationChange()
    );

    const observer = new MutationObserver(() => this.handleNavigationChange());
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  private initDrag(): void {
    this.thumb.addEventListener("mousedown", (e) => {
      if (!this.scrollContainer && !this.isWindowScroll) return;
      e.preventDefault();
      this.isDragging = true;
      this.dragStartY = e.clientY;
      this.dragStartScrollTop = this.isWindowScroll
        ? window.scrollY
        : this.scrollContainer!.scrollTop;
      this.track.classList.add("is-dragging");
      document.body.style.userSelect = "none";
    });

    window.addEventListener("mousemove", (e) => {
      if (!this.isDragging) return;
      const { scrollHeight, clientHeight } = this.getScrollMetrics();
      const trackHeight = this.track.clientHeight;
      const thumbHeight = Math.max(
        (clientHeight / scrollHeight) * trackHeight,
        32
      );
      const maxTop = trackHeight - thumbHeight;
      const ratio = (scrollHeight - clientHeight) / maxTop;
      const delta = (e.clientY - this.dragStartY) * ratio;
      const newScrollTop = this.dragStartScrollTop + delta;

      if (this.isWindowScroll) {
        window.scrollTo(0, newScrollTop);
      } else if (this.scrollContainer) {
        this.scrollContainer.scrollTop = newScrollTop;
      }
    });

    window.addEventListener("mouseup", () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.classList.remove("is-dragging");
      document.body.style.userSelect = "";
    });

    // Click on track outside thumb — jump to position
    this.track.addEventListener("mousedown", (e) => {
      if (e.target === this.thumb) return;
      if (!this.scrollContainer && !this.isWindowScroll) return;
      e.preventDefault();
      const { scrollHeight, clientHeight } = this.getScrollMetrics();
      const trackRect = this.track.getBoundingClientRect();
      const clickRatio = (e.clientY - trackRect.top) / trackRect.height;
      const newScrollTop = clickRatio * (scrollHeight - clientHeight);

      if (this.isWindowScroll) {
        window.scrollTo(0, newScrollTop);
      } else if (this.scrollContainer) {
        this.scrollContainer.scrollTop = newScrollTop;
      }
    });
  }

  private getScrollMetrics(): {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
  } {
    if (this.isWindowScroll) {
      return {
        scrollTop: window.scrollY,
        scrollHeight: document.documentElement.scrollHeight,
        clientHeight: window.innerHeight,
      };
    }
    const el = this.scrollContainer!;
    return {
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
    };
  }

  private handleNavigationChange(): void {
    this.detachFromContainer();
    this.attachToCurrentContainer();
  }

  private attachToCurrentContainer(): void {
    // About page uses lightbox-article structure but scrolls via window, not the panel
    const isAboutPage = document.body.classList.contains(
      "page-template-page-about"
    );

    // Prefer an open lightbox article (but not on about page)
    const lightboxArticle = !isAboutPage
      ? (document.querySelector(".lightbox-article.open") as HTMLElement | null)
      : null;

    // Use item-wrapper only when it's actually the visible scroll container.
    // Excluded cases:
    //   - homepage: the element lingers in the DOM as a hidden lightbox placeholder
    //   - about page: uses lightbox layout structure but window scrolls instead
    const isHomePage = document.body.classList.contains("home");
    const itemWrapper =
      !lightboxArticle && !isHomePage && !isAboutPage
        ? (document.querySelector(".item-wrapper") as HTMLElement | null)
        : null;

    const target = lightboxArticle || itemWrapper;

    // Already attached to this exact container — nothing to do
    if (target && target === this.scrollContainer) return;
    if (!target && this.isWindowScroll) return;

    this.detachFromContainer();

    if (target) {
      // Overlay scroll container
      this.scrollContainer = target;
      this.isWindowScroll = false;
      this.boundUpdate = () => this.updateThumb();
      this.scrollContainer.addEventListener("scroll", this.boundUpdate);
    } else {
      // Fall back to document scroll
      this.scrollContainer = null;
      this.isWindowScroll = true;
      this.boundUpdate = () => this.updateThumb();
      window.addEventListener("scroll", this.boundUpdate);
    }

    this.updateThumb();
    this.showTrack();
  }

  private detachFromContainer(): void {
    if (this.boundUpdate) {
      if (this.isWindowScroll) {
        window.removeEventListener("scroll", this.boundUpdate);
      } else if (this.scrollContainer) {
        this.scrollContainer.removeEventListener("scroll", this.boundUpdate);
      }
    }
    this.scrollContainer = null;
    this.isWindowScroll = false;
    this.boundUpdate = null;
  }

  private updateThumb(): void {
    const { scrollTop, scrollHeight, clientHeight } = this.getScrollMetrics();

    if (scrollHeight <= clientHeight) {
      this.thumb.style.display = "none";
      return;
    }

    this.thumb.style.display = "block";

    const trackHeight = this.track.clientHeight;
    const thumbHeight = Math.max(
      (clientHeight / scrollHeight) * trackHeight,
      32
    );
    const maxTop = trackHeight - thumbHeight;
    const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * maxTop;

    this.thumb.style.height = `${thumbHeight}px`;
    this.thumb.style.top = `${thumbTop}px`;
  }

  private showTrack(): void {
    this.track.style.display = "block";
    this.thumb.style.opacity = "1";
  }
}
